import { Clear, Delete as DeleteIcon, Edit as EditIcon, Email, ExpandMore as ExpandMoreIcon, HelpOutline, LockOpen, Refresh, Search, Visibility as VisibilityIcon } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, AppBar, Box, Button, Divider, Modal, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Toolbar, Tooltip, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, FormControlLabel, Switch, InputLabel, Select, MenuItem, Card, CardHeader, CardContent, Chip, Tabs, Tab } from '@mui/material';
import React, { useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { extendInitiative } from '../../../services/marketingInitiatives';
import { deleteUserByEmail, findByIdOrEmail, sendPasswordResetLink, processStripeSubscription, grantPremiumAccess } from '../../../services/userServices';
import LoadingBackdrop from '../../Common/LoadingBackdrop';
import { REVENUECAT_PROFILE_URL_PREFIX, REVENUECAT_PROJECT_ID_PREFIX } from '../../../constants/revenueCat';
import { FaStripeS } from "react-icons/fa";
import RevenueCatIcon from '../../Elements/RevenueCatIcon';
import MainContentContainer from '../../MainContentContainer/MainContentContainer';

const AppUserPage = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(false);
    const searchInputRef = useRef(null);
    const [expandedLog, setExpandedLog] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [openRevenueCatModal, setOpenRevenueCatModal] = useState(false);
    const [openGrantModal, setOpenGrantModal] = useState(false);
    const [selectedDuration, setSelectedDuration] = useState('1mo');
    const [grantReason, setGrantReason] = useState('');
    const [rcStoreTab, setRcStoreTab] = useState(0);
    const [showExpiredSubs, setShowExpiredSubs] = useState(false);
    const [showSandboxSubs, setShowSandboxSubs] = useState(false);
    const theme = useTheme();

    const handleSearch = async () => {
        if (!searchTerm) return;
        setLoading(true);
        const response = await findByIdOrEmail(searchTerm);
        setUserData(response);
        setLoading(false);

        if (!response) {
            setUserData(null);
            setSearchTerm('');
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleRefresh = async () => {
        if (!searchTerm) return;
        setLoading(true);
        const response = await findByIdOrEmail(searchTerm);
        if (response) {
            setUserData(response);
        } else {
            Swal.fire('Refresh failed', 'Could not re-fetch user data. The displayed data may be stale.', 'warning');
        }
        setLoading(false);
    };

    const handleClearSearch = () => {
        setUserData(null);
        setSearchTerm('');
        setExpandedLog(false);
        setRcStoreTab(0);
        setShowExpiredSubs(false);
        setShowSandboxSubs(false);
        if (searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current.focus();
            }, 500);
        }
    };

    const handleExtendInitiative = (initiative) => {
        const currentEndDate = new Date(initiative.end_date);
        const minDate = new Date(currentEndDate);
        minDate.setDate(minDate.getDate() + 1); // Set minimum date to tomorrow

        Swal.fire({
            title: 'Extend Initiative Duration',
            html: `
                <div style="text-align: left; margin-bottom: 1rem;">
                    <p><strong>Current End Date:</strong> ${currentEndDate.toLocaleDateString()}</p>
                </div>
                <input type="date" id="extensionDate" class="swal2-input" min="${minDate.toISOString().split('T')[0]}">
            `,
            showCancelButton: true,
            confirmButtonText: 'Extend',
            reverseButtons: true,
            didOpen: () => {
                const dateInput = document.getElementById('extensionDate');
                dateInput.value = minDate.toISOString().split('T')[0];
            },
            preConfirm: () => {
                const dateInput = document.getElementById('extensionDate');
                const selectedDate = new Date(dateInput.value);

                if (!dateInput.value) {
                    Swal.showValidationMessage('Please select a date');
                    return false;
                }

                if (selectedDate <= currentEndDate) {
                    Swal.showValidationMessage('New end date must be after current end date');
                    return false;
                }

                const daysToExtend = Math.ceil((selectedDate - currentEndDate) / (1000 * 60 * 60 * 24));
                return daysToExtend;
            }
        }).then(async (result) => {
            if (result.isConfirmed && result.value) {
                const daysToExtend = result.value;
                setActionLoading(true);
                await extendInitiative(initiative, userData.id, daysToExtend, handleExtendInitiativeSuccess);
            }
        });
    };

    const handleExtendInitiativeSuccess = (data, initiativeCode, daysToExtend) => {
        if (data) {
            setUserData({
                ...userData,
                initiatives: userData.initiatives.map(initiative => initiative.id === initiative.id ? {
                    ...initiative,
                    end_date: data.end_date
                } : initiative)
            });

            Swal.fire(
                'Extended!',
                `Initiative ${initiativeCode} will be extended by ${daysToExtend} days (check console for details).`,
                'success'
            );
        }
        setActionLoading(false);
    }

    const handleDeleteUser = () => {
        if (!userData || !userData.email) return;

        Swal.fire({
            icon: "warning",
            text: "Are you sure you want to delete this User?",
            showConfirmButton: true,
            showCancelButton: true,
            reverseButtons: true,
            confirmButtonText: "Delete",
        }).then(async (result) => {
            if (result.value) {
                const deleted = await deleteUserByEmail(userData.email, setActionLoading);
                if (deleted) {
                    setUserData(null);
                    setSearchTerm('');
                }
            }
        });
    };

    const handlePasswordResetLink = () => {
        if (!userData || !userData.email) return;

        Swal.fire({
            icon: "warning",
            text: "Are you sure you want to generate a password reset link for this User?",
            showConfirmButton: true,
            showCancelButton: true,
            reverseButtons: true,
            confirmButtonText: "Generate",
        }).then((result) => {
            if (result.value) {
                sendPasswordResetLink(userData.email, setActionLoading);
            }
        });
    };

    const handleChangeLogAccordion = (panel) => (event, isExpanded) => {
        setExpandedLog(isExpanded ? panel : false);
    };

    const handleOpenRevenueCatModal = () => setOpenRevenueCatModal(true);
    const handleCloseRevenueCatModal = () => setOpenRevenueCatModal(false);

    const openGrantPremiumDialog = () => setOpenGrantModal(true);
    const closeGrantPremiumDialog = () => setOpenGrantModal(false);

    const handleGrantPremium = async () => {
        if (!userData) return;
        setActionLoading(true);
        try {
            const result = await grantPremiumAccess(userData.id, selectedDuration, grantReason);
            if (result) {
                handleSearch();
                closeGrantPremiumDialog();
                setGrantReason('');
            }
        } finally {
            setActionLoading(false);
        }
    };

    const formatPrice = (price, currency) => {
        if (price == null) return null;
        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency', currency: currency || 'USD'
            }).format(price);
        } catch {
            return `${price} ${currency || ''}`.trim();
        }
    };

    const getPeriodTypeLabel = (periodType) => {
        switch (periodType) {
            case 'trial': return 'Free Trial';
            case 'intro': return 'Intro Offer';
            default: return null;
        }
    };

    const formatRevenueCatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        return new Date(timestamp).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
            timeZone: 'UTC',
            timeZoneName: 'shortOffset'
        }).replace('GMT', 'UTC'); // Ensure UTC is clearly stated
    };

    const calculateDaysRemaining = (expirationTimestamp) => {
        if (!expirationTimestamp) return null;
        const now = new Date();
        const expiration = new Date(expirationTimestamp);
        const diffTime = expiration - now;

        // Calculate days, hours, and minutes
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const diffMinutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));

        return {
            days: diffDays,
            hours: diffHours,
            minutes: diffMinutes,
            totalDays: diffTime / (1000 * 60 * 60 * 24) // For color coding
        };
    };

    const formatTimeRemaining = (timeRemaining) => {
        if (!timeRemaining) return '';
        const { days, hours, minutes } = timeRemaining;

        const parts = [];
        if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
        if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
        if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);

        return parts.join(', ');
    };

    const formatAttributeKey = (key) => {
        let formattedKey = key.startsWith('$') ? key.substring(1) : key;
        formattedKey = formattedKey.replace(/_/g, ' ');
        // Camel case to space separated: e.g., appUserID -> App User ID
        formattedKey = formattedKey.replace(/([A-Z])/g, ' $1');
        // Capitalize first letter of each word
        return formattedKey.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
    };

    const getStoreLabel = (store) => {
        const storeLabels = {
            'rc_billing': 'RevenueCat Billing (Web)',
            'app_store': 'App Store',
            'play_store': 'Play Store',
            'stripe': 'Stripe',
            'amazon': 'Amazon',
            'promotional': 'Promotional',
        };
        return storeLabels[store] || store;
    };

    const getSubscriptionStatusColor = (status) => {
        const statusColors = {
            'active': 'success',
            'expired': 'error',
            'trialing': 'info',
            'grace_period': 'warning',
            'billing_retry_period': 'warning',
        };
        return statusColors[status] || 'default';
    };

    const getAutoRenewalLabel = (autoRenewalStatus) => {
        if (autoRenewalStatus === 'will_renew') return { label: 'Will Renew', color: 'success' };
        if (autoRenewalStatus === 'will_not_renew') return { label: 'Will Not Renew', color: 'warning' };
        return { label: autoRenewalStatus || 'Unknown', color: 'default' };
    };

    const renderMetadata = (metadata) => {
        if (!metadata) {
            return <Typography variant="body2">N/A</Typography>;
        }
        if (typeof metadata === 'object' && Object.keys(metadata).length > 0) {
            return (
                <Box component="ul" sx={{ m: 0, p: 0, listStyleType: 'none' }}>
                    {Object.entries(metadata).map(([key, value]) => (
                        <li key={key}>
                            <Typography variant="body2"><strong>{key}:</strong> {String(value)}</Typography>
                        </li>
                    ))}
                </Box>
            );
        }
        return <Typography variant="body2">{String(metadata)}</Typography>;
    };

    return (
        <MainContentContainer>
            <LoadingBackdrop open={loading || actionLoading} />
            <AppBar position="sticky">
                <Toolbar>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        App User Search
                    </Typography>
                </Toolbar>
            </AppBar>

            <Paper sx={{ p: 2, mt: 3, display: 'flex', alignItems: 'center' }}>
                <TextField
                    label="Search by User ID or Email"
                    variant="outlined"
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyUp={handleKeyPress}
                    sx={{ mr: 1 }}
                    disabled={loading || !!userData}
                    inputRef={searchInputRef}
                    autoFocus
                />
                <Button
                    variant="contained"
                    onClick={handleSearch}
                    startIcon={<Search />}
                    disabled={loading || !searchTerm || !!userData}
                    sx={{ mr: 1, px: 3 }}
                >
                    {loading ? 'Searching...' : 'Search'}
                </Button>
                {userData && (
                    <>
                        <Button
                            variant="outlined"
                            onClick={handleRefresh}
                            startIcon={<Refresh />}
                            disabled={loading}
                            sx={{ mr: 1, px: 3 }}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => setUserData(null)}
                            startIcon={<EditIcon />}
                            disabled={loading}
                            sx={{ mr: 1, px: 3 }}
                        >
                            Edit
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleClearSearch}
                            startIcon={<Clear />}
                            disabled={loading}
                            sx={{ px: 3 }}
                        >
                            Clear
                        </Button>
                    </>
                )}
            </Paper>

            {loading && <Typography sx={{ mt: 2 }}>Loading user data...</Typography>}

            {userData && !loading && (
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Card sx={{ mb: 3 }}>
                        <CardHeader
                            title="User Details"
                            sx={{ bgcolor: 'grey.100', borderBottom: 1, borderColor: 'divider' }}
                        />
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                {/* Left: User Info */}
                                <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 2 }}>
                                    {/* Optionally add Avatar here if available */}
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">User ID</Typography>
                                        <Typography variant="body1">{userData.id}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
                                        <Typography variant="body1">{userData.full_name}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                                        <Typography variant="body1">{userData.email}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">Last Platform Used</Typography>
                                        <Typography variant="body1">{userData.os_type}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">Premium Status</Typography>
                                        <Chip
                                            label={(
                                                userData.revenuecat_profile &&
                                                userData.revenuecat_profile.active_entitlements &&
                                                userData.revenuecat_profile.active_entitlements.items.length > 0
                                            ) ? 'Premium' : 'Not Premium'}
                                            color={(
                                                userData.revenuecat_profile &&
                                                userData.revenuecat_profile.active_entitlements &&
                                                userData.revenuecat_profile.active_entitlements.items.length > 0
                                            ) ? 'success' : 'default'}
                                            variant={(
                                                userData.revenuecat_profile &&
                                                userData.revenuecat_profile.active_entitlements &&
                                                userData.revenuecat_profile.active_entitlements.items.length > 0
                                            ) ? 'filled' : 'outlined'}
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    </Box>
                                </Stack>
                                {/* Right: Action Buttons */}
                                <Stack direction="column" spacing={1} alignItems="stretch">
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={handleDeleteUser}
                                        disabled={actionLoading}
                                    >
                                        Delete User
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="revenueCat"
                                        startIcon={<RevenueCatIcon />}
                                        onClick={handleOpenRevenueCatModal}
                                        disabled={!userData.revenuecat_profile || Object.keys(userData.revenuecat_profile).length === 0}
                                    >
                                        RevenueCat Profile
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="info"
                                        startIcon={<Email />}
                                        onClick={handlePasswordResetLink}
                                        disabled={actionLoading}
                                    >
                                        Password Reset Link
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="stripe"
                                        startIcon={<LockOpen />}
                                        onClick={() => {
                                            if (!userData.email) return;
                                            Swal.fire({
                                                title: 'Redeem Stripe Subscription',
                                                input: 'text',
                                                inputLabel: 'Enter Stripe Subscription/Charge ID',
                                                inputPlaceholder: 'e.g., sub_123 or ch_123',
                                                showCancelButton: true,
                                                confirmButtonText: 'Redeem',
                                                reverseButtons: true,
                                                inputValidator: (value) => {
                                                    if (!value) {
                                                        return 'You need to enter a Stripe ID!';
                                                    }
                                                    if (!value.startsWith('sub_') && !value.startsWith('ch_')) {
                                                        return 'Please enter a valid Stripe subscription or charge ID';
                                                    }
                                                    return null;
                                                }
                                            }).then((result) => {
                                                if (result.isConfirmed && result.value) {
                                                    processStripeSubscription(result.value, setActionLoading)
                                                        .then(() => {
                                                            // Refresh user data
                                                            handleSearch();
                                                        })
                                                        .catch(() => {
                                                            // Error is already handled in the service
                                                        });
                                                }
                                            });
                                        }}
                                        disabled={actionLoading}
                                    >
                                        Unlock Stripe
                                    </Button>
                                    {userData.stripe_info?.live_data?.customer?.id && (
                                        <Button
                                            variant="outlined"
                                            color="stripe"
                                            startIcon={<FaStripeS />}
                                            onClick={() => window.open(`https://dashboard.stripe.com/customers/${userData.stripe_info?.live_data?.customer?.id}`, '_blank')}
                                        >
                                            View in Stripe
                                        </Button>
                                    )}
                                    {userData.stripe_info?.live_data?.customer?.id && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block', mt: 1, maxWidth: 400, fontStyle: 'italic' }}
                                        >
                                            Heads up: in Stripe, a <code>user_id</code> in the customer metadata is our ClickFunnels account id (a constant — currently <code>305881</code>) — <strong>not</strong> an app user. Use <code>app_user_id</code> for the real buyer.
                                        </Typography>
                                    )}
                                    {userData.stripe_info?.live_data?.customer?.billing_portal_url && (
                                        <Button
                                            variant="outlined"
                                            color="stripe"
                                            startIcon={<FaStripeS />}
                                            onClick={() => window.open(userData.stripe_info.live_data.customer.billing_portal_url, '_blank')}
                                            sx={{ mt: 2 }}
                                        >
                                            Open Stripe Billing Portal
                                        </Button>
                                    )}
                                    {/* Grant Premium button is only shown if user is NOT already premium (no active entitlements) */}
                                    {!(
                                        userData.revenuecat_profile &&
                                        userData.revenuecat_profile.active_entitlements &&
                                        userData.revenuecat_profile.active_entitlements.items.length > 0
                                    ) && (
                                            <Button
                                                variant="outlined"
                                                color="success"
                                                startIcon={<LockOpen />}
                                                onClick={openGrantPremiumDialog}
                                                disabled={actionLoading}
                                            >
                                                Grant Premium
                                            </Button>
                                        )}
                                </Stack>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            {/* Stripe Information Card */}
                            {userData.stripe_info && (
                                <Card variant="outlined" sx={{ borderLeft: 4, borderColor: 'primary.main', mb: 2 }}>
                                    <CardHeader
                                        title="Stripe Information"
                                        sx={{ bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider', pb: 1, pt: 2 }}
                                    />
                                    <CardContent>
                                        {/* Database Record */}
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Database Record</Typography>
                                        {userData.stripe_info.database_record ? (
                                            <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Customer ID</Typography>
                                                    <Typography variant="body2">{userData.stripe_info.database_record.stripe_customer_id || 'N/A'}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Transaction ID</Typography>
                                                    <Typography variant="body2">{userData.stripe_info.database_record.stripe_transaction_id || 'N/A'}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Subscription Plan</Typography>
                                                    <Typography variant="body2">{userData.stripe_info.database_record.subscription_plan || 'N/A'}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Type</Typography>
                                                    <Typography variant="body2">{
                                                        userData.stripe_info.database_record.is_lifetime ? 'Lifetime' :
                                                            userData.stripe_info.database_record.is_yearly ? 'Yearly' :
                                                                userData.stripe_info.database_record.is_monthly ? 'Monthly' : 'N/A'
                                                    }</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Status</Typography>
                                                    <Box>
                                                        <Chip
                                                            label={userData.stripe_info.database_record.is_trial ? 'Trial' : 'Active'}
                                                            color={userData.stripe_info.database_record.is_trial ? 'info' : 'success'}
                                                            size="small"
                                                        />
                                                    </Box>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Purchased</Typography>
                                                    <Typography variant="body2">{userData.stripe_info.database_record.purchased_at ? new Date(userData.stripe_info.database_record.purchased_at * 1000).toLocaleDateString() : 'N/A'}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Expires</Typography>
                                                    <Typography variant="body2">{userData.stripe_info.database_record.expiration_date ? new Date(userData.stripe_info.database_record.expiration_date * 1000).toLocaleDateString() : 'N/A'}</Typography>
                                                </Box>
                                            </Stack>
                                        ) : (
                                            <Typography color="error">No database record found</Typography>
                                        )}
                                        <Divider sx={{ my: 2, borderColor: 'text.primary' }} />
                                        {/* Live Stripe Data */}
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Live Stripe Data</Typography>
                                        {userData.stripe_info.live_data?.customer ? (
                                            <>
                                                <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">Customer ID</Typography>
                                                        <Typography variant="body2">{userData.stripe_info.live_data.customer.id}</Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">Email</Typography>
                                                        <Typography variant="body2">{userData.stripe_info.live_data.customer.email}</Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">Created</Typography>
                                                        <Typography variant="body2">{new Date(userData.stripe_info.live_data.customer.created * 1000).toLocaleDateString()}</Typography>
                                                    </Box>
                                                </Stack>
                                                {/* Subscriptions */}
                                                <Divider sx={{ my: 2, borderColor: 'text.primary' }} />
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Active Subscriptions:</Typography>
                                                    {userData.stripe_info.live_data.customer.subscriptions?.length > 0 ? (
                                                        <Stack spacing={2}>
                                                            {userData.stripe_info.live_data.customer.subscriptions.map((sub, index) => (
                                                                <Paper
                                                                    key={index}
                                                                    variant="outlined"
                                                                    sx={{
                                                                        p: 1.5,
                                                                        mb: 1,
                                                                        bgcolor: sub.status === 'active' ? 'success.light' : 'warning.light',
                                                                        opacity: 0.95
                                                                    }}
                                                                >
                                                                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                                                        <Box>
                                                                            <Typography variant="body2"><strong>Plan:</strong> {sub.plan.nickname}</Typography>
                                                                            <Chip
                                                                                label={sub.status}
                                                                                color={sub.status === 'active' ? 'success' : sub.status === 'trialing' ? 'info' : sub.status === 'canceled' ? 'error' : 'default'}
                                                                                size="small"
                                                                                sx={{ mt: 0.5 }}
                                                                            />
                                                                            <Typography variant="body2"><strong>Amount:</strong> {(sub.plan.amount / 100).toLocaleString('en-US', {
                                                                                style: 'currency',
                                                                                currency: sub.plan.currency.toUpperCase()
                                                                            })} / {sub.plan.interval}</Typography>
                                                                        </Box>
                                                                        <Box>
                                                                            <Typography variant="body2"><strong>ID:</strong> {sub.id}</Typography>
                                                                            {sub.cancel_at_period_end && (
                                                                                <Typography color="warning.dark" variant="caption">
                                                                                    Will cancel at period end
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    </Stack>
                                                                    <Divider sx={{ my: 1, borderColor: 'text.primary' }} />
                                                                    <Box>
                                                                        <Typography variant="body2"><strong>Current Period:</strong></Typography>
                                                                        <Stack direction="row" spacing={2} sx={{ ml: 2 }}>
                                                                            <Typography variant="caption">Start: {new Date(sub.current_period_start * 1000).toLocaleString()}</Typography>
                                                                            <Typography variant="caption">End: {new Date(sub.current_period_end * 1000).toLocaleString()}</Typography>
                                                                        </Stack>
                                                                    </Box>
                                                                </Paper>
                                                            ))}
                                                        </Stack>
                                                    ) : (
                                                        <Box sx={{ mt: 3, mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                            <LockOpen color="disabled" sx={{ fontSize: 40, mb: 1 }} />
                                                            <Typography color="text.secondary" align="center">No active subscriptions</Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </>
                                        ) : (
                                            <Typography color="error">No live Stripe data found</Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            )}
                        </CardContent>
                    </Card>

                    {/* RC Subscriptions Card */}
                    {userData.revenuecat_subscriptions?.items?.length > 0 && (() => {
                        const allSubs = userData.revenuecat_subscriptions.items;
                        const storeGroups = allSubs.reduce((acc, sub) => {
                            const store = sub.store || 'unknown';
                            if (!acc[store]) acc[store] = [];
                            acc[store].push(sub);
                            return acc;
                        }, {});
                        const storeKeys = Object.keys(storeGroups);
                        const safeTabIndex = rcStoreTab < storeKeys.length ? rcStoreTab : 0;
                        const currentStoreKey = storeKeys[safeTabIndex];
                        const currentSubs = storeGroups[currentStoreKey] || [];
                        const filteredSubs = currentSubs
                            .filter(s => showExpiredSubs || s.status !== 'expired')
                            .filter(s => showSandboxSubs || s.environment !== 'sandbox');

                        return (
                            <Card variant="outlined" sx={{ borderLeft: 4, borderColor: 'revenueCat.main', mb: 2 }}>
                                <CardHeader
                                    title="RevenueCat Subscriptions"
                                    avatar={<RevenueCatIcon />}
                                    sx={{ bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider', pb: 1, pt: 2 }}
                                    action={
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={showExpiredSubs}
                                                        onChange={(e) => setShowExpiredSubs(e.target.checked)}
                                                    />
                                                }
                                                label={<Typography variant="caption">Show Expired</Typography>}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={showSandboxSubs}
                                                        onChange={(e) => setShowSandboxSubs(e.target.checked)}
                                                    />
                                                }
                                                label={<Typography variant="caption">Show Sandbox</Typography>}
                                            />
                                            {userData.revenuecat_profile?.id && (
                                                <Button
                                                    size="small"
                                                    color="revenueCat"
                                                    onClick={() => window.open(
                                                        `${REVENUECAT_PROFILE_URL_PREFIX}${userData.revenuecat_profile.project_id.replace(REVENUECAT_PROJECT_ID_PREFIX, '')}/${userData.revenuecat_profile.id}`,
                                                        '_blank'
                                                    )}
                                                >
                                                    View in RevenueCat
                                                </Button>
                                            )}
                                        </Stack>
                                    }
                                />
                                <Tabs
                                    value={safeTabIndex}
                                    onChange={(_, newValue) => setRcStoreTab(newValue)}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                                >
                                    {storeKeys.map((store) => {
                                        const visibleCount = storeGroups[store]
                                            .filter(s => showExpiredSubs || s.status !== 'expired')
                                            .filter(s => showSandboxSubs || s.environment !== 'sandbox')
                                            .length;
                                        return (
                                            <Tab
                                                key={store}
                                                label={`${getStoreLabel(store)} (${visibleCount}/${storeGroups[store].length})`}
                                            />
                                        );
                                    })}
                                </Tabs>
                                <CardContent>
                                    {currentStoreKey === 'stripe' && (
                                        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                These come from external Stripe checkout pages (e.g. ClickFunnels / flash sale) and should match the <strong>Stripe</strong> section above.
                                            </Typography>
                                        </Box>
                                    )}
                                    {filteredSubs.length > 0 ? (
                                        <Stack spacing={2}>
                                            {filteredSubs.map((sub, index) => {
                                                const periodEnd = sub.current_period_end ? sub.current_period_end : null;
                                                const timeRemaining = periodEnd ? calculateDaysRemaining(periodEnd) : null;
                                                const autoRenewal = getAutoRenewalLabel(sub.auto_renewal_status);

                                                return (
                                                    <Paper
                                                        key={index}
                                                        variant="outlined"
                                                        sx={{
                                                            p: 1.5,
                                                            bgcolor: sub.status === 'active' ? 'success.light' :
                                                                sub.status === 'expired' ? 'grey.100' : 'grey.50',
                                                            opacity: sub.status === 'expired' ? 0.7 : 0.95
                                                        }}
                                                    >
                                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                                            <Typography variant="body2">
                                                                <strong>{sub.display_name || sub.product_id}</strong>
                                                                {sub.display_name && (
                                                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                                                        ({sub.product_id})
                                                                    </Typography>
                                                                )}
                                                            </Typography>
                                                            <Chip
                                                                label={sub.status}
                                                                color={getSubscriptionStatusColor(sub.status)}
                                                                size="small"
                                                            />
                                                            {sub.status !== 'expired' && (
                                                                <Chip
                                                                    label={autoRenewal.label}
                                                                    color={autoRenewal.color}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                            {getPeriodTypeLabel(sub.period_type) && (
                                                                <Chip
                                                                    label={getPeriodTypeLabel(sub.period_type)}
                                                                    color={sub.period_type === 'trial' ? 'info' : 'secondary'}
                                                                    size="small"
                                                                />
                                                            )}
                                                        </Stack>
                                                        <Divider sx={{ my: 1 }} />
                                                        <Box>
                                                            <Typography variant="body2"><strong>Current Period:</strong></Typography>
                                                            <Stack direction="row" spacing={2} sx={{ ml: 2 }}>
                                                                <Typography variant="caption">
                                                                    Start: {sub.current_period_start ? formatRevenueCatTimestamp(sub.current_period_start) : 'N/A'}
                                                                </Typography>
                                                                <Typography variant="caption">
                                                                    End: {periodEnd ? formatRevenueCatTimestamp(periodEnd) : 'N/A'}
                                                                </Typography>
                                                            </Stack>
                                                            {timeRemaining && timeRemaining.totalDays > 0 && (
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        ml: 2,
                                                                        color: timeRemaining.totalDays <= 7 ? 'error.main' :
                                                                            timeRemaining.totalDays <= 30 ? 'warning.main' : 'success.main',
                                                                        fontWeight: 'bold'
                                                                    }}
                                                                >
                                                                    ({formatTimeRemaining(timeRemaining)} remaining)
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        {(sub.original_purchase_date || sub.price != null) && (
                                                            <Box sx={{ mt: 1 }}>
                                                                {sub.original_purchase_date && (
                                                                    <Typography variant="caption" sx={{ ml: 2, display: 'block' }}>
                                                                        Original Purchase: {formatRevenueCatTimestamp(sub.original_purchase_date)}
                                                                    </Typography>
                                                                )}
                                                                {sub.price != null && (
                                                                    <Typography variant="caption" sx={{ ml: 2, display: 'block' }}>
                                                                        Price: {formatPrice(sub.price, sub.currency)}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        )}
                                                        {sub.management_url && (
                                                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Tooltip title="Sends an email to the user with a link to log in to RevenueCat's customer portal, where they can manage their subscription (cancel, change plan, update payment method).">
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        onClick={() => window.open(sub.management_url, '_blank')}
                                                                    >
                                                                        Manage Subscription
                                                                    </Button>
                                                                </Tooltip>
                                                                <Tooltip title="RevenueCat docs: Managing Customer Subscriptions">
                                                                    <Button
                                                                        size="small"
                                                                        variant="text"
                                                                        startIcon={<HelpOutline />}
                                                                        onClick={() => window.open('https://www.revenuecat.com/docs/web/web-billing/managing-customer-subscriptions', '_blank')}
                                                                    >
                                                                        Docs
                                                                    </Button>
                                                                </Tooltip>
                                                            </Box>
                                                        )}
                                                    </Paper>
                                                );
                                            })}
                                        </Stack>
                                    ) : (
                                        <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                                            No {showExpiredSubs ? '' : 'active '}{showSandboxSubs ? '' : 'production '}subscriptions for {getStoreLabel(currentStoreKey)}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })()}

                    {userData.revenuecat_profile && (
                        <Modal
                            open={openRevenueCatModal}
                            onClose={handleCloseRevenueCatModal}
                            aria-labelledby="revenuecat-profile-modal-title"
                            aria-describedby="revenuecat-profile-modal-description"
                        >
                            <Box sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '80%',
                                maxWidth: 700,
                                bgcolor: 'background.paper',
                                borderRadius: '8px',
                                boxShadow: 24,
                                display: 'flex',
                                flexDirection: 'column',
                                maxHeight: '90vh',
                            }}>
                                <Typography id="revenuecat-profile-modal-title" variant="h6" component="h2" sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                    RevenueCat Profile for {userData.email}
                                </Typography>
                                <Box sx={{ overflowY: 'auto', flexGrow: 1, p: 2 }}>
                                    <Typography variant="h6" component="h3" sx={{ mt: 0, mb: 1, fontSize: '1.1rem', fontWeight: 'bold' }}>
                                        Customer Details
                                    </Typography>
                                    <Paper variant="outlined" sx={{ background: theme.palette.grey[50], p: 1.5, mb: 2 }}>
                                        {userData.revenuecat_profile.active_entitlements && userData.revenuecat_profile.active_entitlements.items.length > 0 ? (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="h6" component="h3" sx={{ mb: 1, fontSize: '1.1rem', fontWeight: 'bold' }}>
                                                    Active Entitlements / Premium Access
                                                </Typography>
                                                <Paper variant="outlined" sx={{ background: theme.palette.grey[50], p: 1.5 }}>
                                                    {userData.revenuecat_profile.active_entitlements.items.map((entitlement, index) => {
                                                        const timeRemaining = calculateDaysRemaining(entitlement.expires_at);
                                                        return (
                                                            <React.Fragment key={`entitlement-${index}`}>
                                                                <Box sx={{ mb: 1 }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <Typography variant="body1" component="div" sx={{ wordBreak: 'break-all' }}>
                                                                            {formatRevenueCatTimestamp(entitlement.expires_at)}
                                                                        </Typography>
                                                                        {timeRemaining !== null && (
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    color: timeRemaining.totalDays <= 7 ? 'error.main' :
                                                                                        timeRemaining.totalDays <= 30 ? 'warning.main' : 'success.main',
                                                                                    fontWeight: 'bold'
                                                                                }}
                                                                            >
                                                                                ({formatTimeRemaining(timeRemaining)} remaining)
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                    <Typography variant="caption" color="textSecondary">Expires At</Typography>
                                                                </Box>
                                                                {index < userData.revenuecat_profile.attributes.length - 1 && <Divider sx={{ my: 1 }} />}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </Paper>
                                            </Box>
                                        ) : (
                                            <Typography variant="body1" component="div" sx={{ wordBreak: 'break-all' }}>
                                                User Is Not Premium
                                            </Typography>
                                        )}
                                    </Paper>
                                    <Paper variant="outlined" sx={{ background: theme.palette.grey[50], p: 1.5, mb: 2 }}>
                                        <Box sx={{ mb: 1 }}>
                                            <Typography variant="body1" component="div">{userData.revenuecat_profile.id}</Typography>
                                            <Typography variant="caption" color="textSecondary">App user ID</Typography>
                                        </Box>
                                        <Divider sx={{ my: 1 }} />
                                        <Box sx={{ mb: 1 }}>
                                            <Typography variant="body1" component="div">{formatRevenueCatTimestamp(userData.revenuecat_profile.first_seen_at)}</Typography>
                                            <Typography variant="caption" color="textSecondary">First seen</Typography>
                                        </Box>
                                        <Divider sx={{ my: 1 }} />
                                        <Box>
                                            <Typography variant="body1" component="div">{formatRevenueCatTimestamp(userData.revenuecat_profile.last_seen_at)}</Typography>
                                            <Typography variant="caption" color="textSecondary">Last seen</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body1" component="div">
                                                <a href={`${REVENUECAT_PROFILE_URL_PREFIX}${userData.revenuecat_profile.project_id.replace(REVENUECAT_PROJECT_ID_PREFIX, '')}/${userData.revenuecat_profile.id}`} target="_blank" rel="noopener noreferrer">
                                                    View in RevenueCat
                                                </a>
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">Revenue Profile Link</Typography>
                                        </Box>
                                    </Paper>

                                    {userData.revenuecat_profile.attributes && userData.revenuecat_profile.attributes.items.length > 0 && (
                                        <>
                                            <Typography variant="h6" component="h3" sx={{ mb: 1, fontSize: '1.1rem', fontWeight: 'bold' }}>
                                                Attributes
                                            </Typography>
                                            <Paper variant="outlined" sx={{ background: theme.palette.grey[50], p: 1.5 }}>
                                                {userData.revenuecat_profile.attributes.items.map((attribute, index) => (
                                                    <React.Fragment key={`attribute-${index}`}>
                                                        <Box sx={{ mb: 1 }}>
                                                            <Typography variant="body1" component="div" sx={{ wordBreak: 'break-all' }}>
                                                                {attribute.value}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">{attribute.name}</Typography>
                                                        </Box>
                                                        {index < userData.revenuecat_profile.attributes.length - 1 && <Divider sx={{ my: 1 }} />}
                                                    </React.Fragment>
                                                ))}
                                            </Paper>
                                        </>
                                    )}
                                </Box>
                                <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button onClick={handleCloseRevenueCatModal}>Close</Button>
                                </Box>
                            </Box>
                        </Modal>
                    )}

                    {/* Grant Premium Dialog */}
                    <Dialog open={openGrantModal} onClose={closeGrantPremiumDialog} maxWidth="xs" fullWidth>
                        <DialogTitle>Grant Premium Access</DialogTitle>
                        <DialogContent dividers>
                            <FormControl fullWidth sx={{ mt: 1 }}>
                                <InputLabel id="duration-label">Duration</InputLabel>
                                <Select
                                    labelId="duration-label"
                                    value={selectedDuration}
                                    label="Duration"
                                    onChange={(e) => setSelectedDuration(e.target.value)}
                                >
                                    <MenuItem value="1wk">1 Week</MenuItem>
                                    <MenuItem value="2wk">2 Weeks</MenuItem>
                                    <MenuItem value="1mo">1 Month</MenuItem>
                                    <MenuItem value="3mo">3 Months</MenuItem>
                                    <MenuItem value="6mo">6 Months</MenuItem>
                                    <MenuItem value="1yr">1 Year</MenuItem>
                                    <MenuItem value="lifetime">Lifetime</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="Reason"
                                value={grantReason}
                                onChange={e => setGrantReason(e.target.value)}
                                fullWidth
                                sx={{ mt: 2 }}
                                placeholder="e.g. Manual grant, support, etc."
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeGrantPremiumDialog}>Cancel</Button>
                            <Button variant="contained" onClick={handleGrantPremium} disabled={actionLoading}>Grant</Button>
                        </DialogActions>
                    </Dialog>

                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>Marketing Initiatives</Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell><strong>Name</strong></TableCell>
                                        <TableCell><strong>Code</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell><strong>Start Date</strong></TableCell>
                                        <TableCell><strong>End Date</strong></TableCell>
                                        <TableCell><strong>Channel</strong></TableCell>
                                        <TableCell align="right"><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {userData.initiatives && userData.initiatives.length > 0 ? userData.initiatives.map((initiative) => (
                                        <TableRow key={`init-${initiative.id}`}>
                                            <TableCell>{initiative.name}</TableCell>
                                            <TableCell>{initiative.code}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ textTransform: 'capitalize', color: initiative.status === 'active' ? 'green' : initiative.status === 'expired' ? 'red' : 'inherit' }}>
                                                    {initiative.status}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{initiative.start_date ? new Date(initiative.start_date).toLocaleDateString() : 'N/A'}</TableCell>
                                            <TableCell>{initiative.end_date ? new Date(initiative.end_date).toLocaleDateString() : 'N/A'}</TableCell>
                                            <TableCell>{initiative.subscription_channel || 'N/A'}</TableCell>
                                            <TableCell align="right">
                                                {
                                                    initiative.is_active !== 0 && initiative.deleted_at === null ? (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleExtendInitiative(initiative)}
                                                            disabled={actionLoading}
                                                        >
                                                            Extend Initiative
                                                        </Button>
                                                    ) : null
                                                }
                                            </TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">No initiatives found for this user.</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    {userData.logs && userData.logs.length > 0 && (
                        <Accordion expanded={expandedLog === 'logsPanel'} onChange={handleChangeLogAccordion('logsPanel')}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="logsPanelbh-content"
                                id="logsPanelbh-header"
                            >
                                <Typography variant="h6">User History Logs ({userData.logs.length})</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TableContainer component={Paper}>
                                    <Table size="small" aria-label="user history logs">
                                        <TableHead sx={{ backgroundColor: '#f9f9f9' }}>
                                            <TableRow>
                                                <TableCell><strong>Timestamp</strong></TableCell>
                                                <TableCell><strong>Request Source</strong></TableCell>
                                                <TableCell><strong>Status</strong></TableCell>
                                                <TableCell><strong>Requested By</strong></TableCell>
                                                <TableCell><strong>IP Address</strong></TableCell>
                                                <TableCell sx={{ width: '30%' }}><strong>Action (Metadata)</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {userData.logs.map((log) => (
                                                <TableRow key={`log-${log.id}`}>
                                                    <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                                                    <TableCell>{log.request_source}</TableCell>
                                                    <TableCell>{log.request_status}</TableCell>
                                                    <TableCell>{log.requested_by}</TableCell>
                                                    <TableCell>{log.ip_address}</TableCell>
                                                    <TableCell>{renderMetadata(log.metadata)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Box>
            )}
        </MainContentContainer>
    );
};

export default AppUserPage;
